/**
 * Expectation view with responsive layout (no card shell)
 */

import { formatVND } from '@/utils/currencyUtils'
import { formatLocalDate } from '@/utils/dateUtils'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { IconEdit, IconTrash } from '@tabler/icons-react'
import type { PriceExpectation } from '@/types/expectation'
import { useAssets } from '@/services/api/assets'
import { useGoldPrices } from '@/services/api/goldPrice'
import { useMemo } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

interface ExpectationCardProps {
  expectation: PriceExpectation
  currentPrice?: number
  onEdit?: (expectation: PriceExpectation) => void
  onDelete?: (expectation: PriceExpectation) => void
}

const formatCompactVND = (value: number | undefined) => {
  if (value === undefined || Number.isNaN(value)) return 'N/A'
  try {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value)
  } catch {
    return formatVND(value)
  }
}

export function ExpectationCard({ expectation, currentPrice, onEdit, onDelete }: ExpectationCardProps) {
  const { data: holdings = [] } = useAssets('holding')
  const { data: goldTypes = [] } = useGoldPrices('Hồ Chí Minh')

  const qtyByType = useMemo(() => {
    return holdings.reduce<Record<number, number>>((acc, asset) => {
      if (asset.goldTypeId !== undefined) {
        acc[asset.goldTypeId] = (acc[asset.goldTypeId] || 0) + asset.quantity
      }
      return acc
    }, {})
  }, [holdings])

  const purchaseByType = useMemo(() => {
    return holdings.reduce<Record<number, { qty: number; total: number }>>((acc, asset) => {
      if (asset.goldTypeId !== undefined) {
        const current = acc[asset.goldTypeId] || { qty: 0, total: 0 }
        const qty = asset.quantity || 0
        const price = asset.purchasePrice || 0
        acc[asset.goldTypeId] = { qty: current.qty + qty, total: current.total + price * qty }
      }
      return acc
    }, {})
  }, [holdings])

  const priceByType = useMemo(() => {
    return goldTypes.reduce<Record<number, { sell: number; label: string; sellLabel: string }>>((acc, type) => {
      acc[type.Id] = { sell: type.SellValue, label: type.TypeName, sellLabel: type.Sell }
      return acc
    }, {})
  }, [goldTypes])

  const expectedPrice =
    typeof expectation.expectedPrice === 'number' && !isNaN(expectation.expectedPrice) && isFinite(expectation.expectedPrice)
      ? expectation.expectedPrice
      : 0

  let targetDate: Date
  let isValidDate = false
  try {
    targetDate = new Date(expectation.targetDate)
    isValidDate = !isNaN(targetDate.getTime())
  } catch {
    targetDate = new Date()
    isValidDate = false
  }

  const isMet = currentPrice !== undefined && currentPrice >= expectedPrice
  const isExceeded = currentPrice !== undefined && currentPrice > expectedPrice * 1.1
  const isPastTarget = isValidDate && targetDate < new Date()

  const getStatusBadge = () => {
    if (isExceeded) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400">
          Vượt mục tiêu
        </span>
      )
    }
    if (isMet) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-400">
          Đạt mục tiêu
        </span>
      )
    }
    if (isPastTarget) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
          Quá hạn
        </span>
      )
    }
    return (
      <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-400">
        Đang chờ
      </span>
    )
  }

  const progressPercent = currentPrice && expectedPrice > 0 ? Math.min((currentPrice / expectedPrice) * 100, 100) : 0

  const perType =
    expectation.assetPrices?.map((ap) => {
      const qty = qtyByType[ap.goldTypeId] || 0
      const priceInfo = priceByType[ap.goldTypeId]
      const currentSell = priceInfo?.sell
      const purchase = purchaseByType[ap.goldTypeId]
      const purchaseAvg = purchase && purchase.qty > 0 ? purchase.total / purchase.qty : undefined
      const purchaseTotal = purchase?.total || 0
      const plVsExpectationValue = currentSell ? (currentSell - ap.expectedPrice) * qty : 0
      const plVsExpectationPct = currentSell ? ((currentSell - ap.expectedPrice) / ap.expectedPrice) * 100 : undefined
      const plVsPurchaseValue = purchaseAvg !== undefined && currentSell ? (currentSell - purchaseAvg) * qty : 0
      const plVsPurchasePct =
        purchaseAvg !== undefined && currentSell ? ((currentSell - purchaseAvg) / purchaseAvg) * 100 : undefined
      return {
        goldTypeId: ap.goldTypeId,
        name: priceInfo?.label ?? `Loại vàng ${ap.goldTypeId}`,
        expected: ap.expectedPrice,
        current: currentSell,
        currentLabel: priceInfo?.sellLabel,
        qty,
        purchaseAvg,
        purchaseTotal,
        plVsExpectationValue,
        plVsExpectationPct,
        plVsPurchaseValue,
        plVsPurchasePct,
      }
    }) ?? []

  const totals = perType.reduce(
    (acc, row) => {
      const expectedTotal = row.expected * row.qty
      const currentTotal = row.current ? row.current * row.qty : 0
      const purchaseTotal = row.purchaseTotal || 0
      acc.purchase += purchaseTotal
      acc.expected += expectedTotal
      acc.current += currentTotal
      acc.deltaVsExpectation += currentTotal - expectedTotal
      acc.deltaVsPurchase += currentTotal - purchaseTotal
      return acc
    },
    { expected: 0, current: 0, deltaVsExpectation: 0, purchase: 0, deltaVsPurchase: 0 }
  )
  const totalDeltaExpectationPct = totals.expected ? (totals.deltaVsExpectation / totals.expected) * 100 : 0
  const totalDeltaPurchasePct = totals.purchase ? (totals.deltaVsPurchase / totals.purchase) * 100 : 0

  return (
    <div
      className={cn(
        'border border-border/60 rounded-lg bg-background/60 p-4 md:p-6 space-y-4 shadow-sm',
        isMet && 'border-green-500'
      )}
    >
      <div className="grid gap-4 md:grid-cols-[1.1fr,0.9fr] items-start">
        <div className="space-y-4 min-w-0">
          <div className="flex flex-wrap items-start gap-3 justify-between">
            <div className="space-y-1 min-w-0">
              <h3 className="text-lg font-semibold leading-snug truncate">{expectation.name}</h3>
              <p className="text-sm text-muted-foreground break-words">
                Giá kỳ vọng chung: {formatVND(expectedPrice)}
              </p>
              <p className="text-xs text-muted-foreground break-words">
                Ngày mục tiêu: {isValidDate ? formatLocalDate(expectation.targetDate) : 'N/A'} • {expectation.assetPrices?.length ?? 0} loại vàng
              </p>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              {currentPrice !== undefined && (
                <div className="text-right min-w-0">
                  <p className="text-xs text-muted-foreground">Giá hiện tại</p>
                  <p className="text-sm font-semibold truncate">{formatVND(currentPrice)}</p>
                </div>
              )}
              {getStatusBadge()}
            </div>
          </div>

          {currentPrice !== undefined && (
            <div className="space-y-1">
              <div className="w-full bg-muted rounded-full h-2">
                <div className={cn('h-2 rounded-full transition-all', isMet ? 'bg-green-500' : 'bg-primary')} style={{ width: `${progressPercent}%` }} />
              </div>
              <p className="text-xs text-muted-foreground">{progressPercent.toFixed(1)}% tiến độ</p>
            </div>
          )}

          <div className="space-y-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="self-start">
                  Xem chi tiết {perType.length} loại vàng
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[92vw] max-w-4xl p-0" side="bottom" align="start">
                <div className="max-h-[360px] overflow-auto">
                  <Table className="min-w-[800px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Loại vàng</TableHead>
                        <TableHead className="text-right">Giá mua</TableHead>
                        <TableHead className="text-right">Giá kỳ vọng</TableHead>
                        <TableHead className="text-right">Giá hiện tại</TableHead>
                        <TableHead className="text-right">SL</TableHead>
                        <TableHead className="text-right">Lãi/lỗ</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {perType.map((row) => (
                        <TableRow key={row.goldTypeId}>
                          <TableCell className="font-medium">{row.name}</TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {row.purchaseAvg !== undefined ? formatVND(row.purchaseAvg) : 'N/A'}
                          </TableCell>
                          <TableCell className="text-right">{formatVND(row.expected)}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{row.currentLabel ?? 'N/A'}</TableCell>
                          <TableCell className="text-right">{row.qty}</TableCell>
                          <TableCell className="text-right space-y-0.5">
                            <p className={cn('text-xs', row.plVsPurchaseValue >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                              So với mua: {row.current ? formatVND(row.plVsPurchaseValue) : 'N/A'}
                              {typeof row.plVsPurchasePct === 'number' && (
                                <span className="text-[11px] text-muted-foreground"> ({row.plVsPurchasePct.toFixed(1)}%)</span>
                              )}
                            </p>
                            <p className={cn('text-xs', row.plVsExpectationValue >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                              So với kỳ vọng: {row.current ? formatVND(row.plVsExpectationValue) : 'N/A'}
                              {typeof row.plVsExpectationPct === 'number' && (
                                <span className="text-[11px] text-muted-foreground"> ({row.plVsExpectationPct.toFixed(1)}%)</span>
                              )}
                            </p>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="p-3 border rounded-md bg-muted/40 space-y-3">
          <div className="text-sm font-semibold">Tổng quan</div>
          <div className="space-y-3 text-sm leading-snug">
            <div className="flex items-start justify-between gap-2">
              <div className="text-muted-foreground">Tổng giá trị mua</div>
              <div className="text-right">
                <div className="font-semibold">{formatCompactVND(totals.purchase)}</div>
                <div className={cn('text-xs', totals.deltaVsPurchase >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                  {formatCompactVND(totals.deltaVsPurchase)} ({totalDeltaPurchasePct.toFixed(1)}%)
                </div>
              </div>
            </div>
            <div className="flex items-start justify-between gap-2">
              <div className="text-muted-foreground">Tổng giá trị kỳ vọng</div>
              <div className="text-right">
                <div className="font-semibold">{formatCompactVND(totals.expected)}</div>
                <div className="text-xs text-muted-foreground">Theo giá kỳ vọng nhập</div>
              </div>
            </div>
            <div className="flex items-start justify-between gap-2">
              <div className="text-muted-foreground">Tổng giá trị hiện tại</div>
              <div className="text-right">
                <div className="font-semibold">{formatCompactVND(totals.current)}</div>
                <div className={cn('text-xs', totals.deltaVsExpectation >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                  {formatCompactVND(totals.deltaVsExpectation)} ({totalDeltaExpectationPct.toFixed(1)}%)
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(onEdit || onDelete) && (
        <div className="flex gap-2 pt-0">
          {onEdit && (
            <Button onClick={() => onEdit(expectation)} variant="outline" className="flex-1 min-h-[44px]" size="sm">
              <IconEdit className="h-4 w-4 mr-2" stroke={1.5} />
              Sửa
            </Button>
          )}
          {onDelete && (
            <Button onClick={() => onDelete(expectation)} variant="destructive" className="flex-1 min-h-[44px]" size="sm">
              <IconTrash className="h-4 w-4 mr-2" stroke={1.5} />
              Xóa
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
